import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  User, Calendar, MapPin, Phone, Mail, Lock, 
  Eye, EyeOff, UserCheck, Shield, Key,
  CheckCircle2, Info, Camera
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from './ui/toast/use-toast';
import LocationAutocomplete from './LocationAutocomplete';
import BaseRegister from './register/BaseRegister';

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { 
    opacity: 1, 
    transition: { 
      duration: 0.6, 
      ease: "easeOut" 
    } 
  }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const StaffRegister = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const totalSteps = 4;

  // Estados
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});

  // Estados de verificación
  const [verificationState, setVerificationState] = useState({
    loading: false,
    codeSent: false,
    error: null
  });
  const [verificationCode, setVerificationCode] = useState('');
  const [phoneCodeSent, setPhoneCodeSent] = useState(false);
  const [phoneVerificationCode, setPhoneVerificationCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(0);
  const [resendDisabled, setResendDisabled] = useState(false);

  // Datos del formulario
  const [formData, setFormData] = useState({
    userType: 'staff',
    nombre: '',
    fechaNacimiento: '',
    genero: '',
    fotoPerfil: null,
    fotoPreview: null,
    ubicacion: '',
    ubicacionData: null,
    email: '',
    emailVerified: false,
    telefono: '',
    prefijo_telefono: '+51',
    phoneVerified: false,
    puesto: '',
    departamento: '',
    nivelAcceso: 'basico',
    password: '',
    confirmPassword: '',
    codigoStaff: ''
  });

  // Helper function for error handling fetch requests
  const handleFetchError = (error, defaultMessage = 'Error de conexión') => {
    console.error('Network Error:', error);
    
    const errorMessage = error.message === 'Failed to fetch' 
      ? defaultMessage 
      : error.message;

    toast({
      title: "Error de Conexión",
      description: errorMessage,
      variant: "destructive"
    });

    return null;
  };

  // Timer para reenvío de código
  useEffect(() => {
    let timer;
    if (timeLeft > 0) {
      timer = setInterval(() => {
        setTimeLeft(prev => prev - 1);
      }, 1000);
    } else {
      setResendDisabled(false);
    }
    return () => clearInterval(timer);
  }, [timeLeft]);

  // Manejadores de cambios en formulario
  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Limpiar errores al cambiar el valor
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }

    // Validaciones específicas
    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(value)) {
        try {
          const response = await fetch(`https://backend-production-1786.up.railway.app/api/auth/check-email/${value}`, {
            method: 'GET',
            headers: {
              'Accept': 'application/json'
            }
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            throw new Error(errorData.message || 'Error al verificar email');
          }

          const data = await response.json();
          if (data.exists) {
            setErrors(prev => ({
              ...prev,
              email: 'Este email ya está registrado'
            }));
          }
        } catch (error) {
          handleFetchError(error, 'No se pudo verificar la disponibilidad del email');
        }
      }
    }

    if (name === 'telefono' && value.length === 9) {
      try {
        const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/check-phone', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            telefono: value,
            prefijo: formData.prefijo_telefono
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData.message || 'Error al verificar teléfono');
        }

        const data = await response.json();
        if (!data.available) {
          setErrors(prev => ({
            ...prev,
            telefono: 'Este teléfono ya está registrado'
          }));
        }
      } catch (error) {
        handleFetchError(error, 'No se pudo verificar la disponibilidad del teléfono');
      }
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setErrors(prev => ({ ...prev, fotoPerfil: 'La imagen no debe superar 5MB' }));
        return;
      }

      // Mostrar preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ 
          ...prev, 
          fotoPerfil: file,
          fotoPreview: reader.result
        }));
      };
      reader.readAsDataURL(file);

      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.fotoPerfil;
        return newErrors;
      });
    }
  };

  const handleLocationSelect = (location) => {
    setFormData(prev => ({
      ...prev,
      ubicacion: `${location.city}, ${location.province}, ${location.country}`,
      ciudad: location.city,
      provincia: location.province,
      pais: location.country
    }));
    
    if (errors.location) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.location;
        return newErrors;
      });
    }
  };

  const verifyCode = async (type, code) => {
    if (code.length !== 6) return;
  
    try {
      setLoading(true);
      const phoneNumber = formData.telefono;
      const prefijo = formData.prefijo_telefono;
  
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/verify-sms', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          telefono: phoneNumber,
          prefijo: prefijo,
          code: code
        })
      });
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Código inválido');
      }
  
      const data = await response.json();
  
      setFormData(prev => ({ ...prev, phoneVerified: true }));
      setPhoneCodeSent(false);
      
      toast({
        title: "Teléfono verificado",
        description: "Tu número de teléfono ha sido verificado correctamente",
      });
  
      handleNext();
  
    } catch (error) {
      console.error('Error en verificación:', error);
      toast({
        title: "Error",
        description: error.message || "Error al verificar el código",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Manejadores de verificación de email
  const requestVerificationCode = async () => {
    if (!formData.email) {
      setErrors(prev => ({ ...prev, email: 'El email es requerido' }));
      return;
    }
  
    const controller = new AbortController();
    
    setVerificationState({
      loading: true,
      codeSent: false,
      error: null
    });
  
    try {
      // Configurar un timeout más largo
      const timeoutId = setTimeout(() => {
        controller.abort('La solicitud tardó demasiado');
      }, 15000); // 15 segundos
  
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/request-email-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email.trim(),
          userType: 'staff'
        }),
        signal: controller.signal
      });
  
      clearTimeout(timeoutId);
  
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Error en la verificación');
      }
  
      const data = await response.json();
  
      setVerificationState({
        loading: false,
        codeSent: true,
        error: null
      });
  
      toast({
        title: "Código enviado",
        description: "Revisa tu correo electrónico para el código de verificación",
      });
  
      // Iniciar temporizador para reenvío
      setTimeLeft(600); // 10 minutos
      setResendDisabled(true);
  
      // En desarrollo, mostrar el código
      if (process.env.NODE_ENV === 'development' && data.code) {
        console.log('Código de verificación (desarrollo):', data.code);
      }
  
    } catch (error) {
      console.error('Error detallado al solicitar código:', error);
      
      // Manejar específicamente errores de cancelación
      if (error.name === 'AbortError') {
        toast({
          title: "Tiempo de espera agotado",
          description: "La solicitud tardó demasiado. Por favor, verifica tu conexión e inténtalo de nuevo.",
          variant: "destructive"
        });
      } else {
        // Manejo detallado de errores de red
        const errorMessage = error.message || 'No se pudo enviar el código de verificación';
        
        toast({
          title: "Error de Conexión",
          description: errorMessage,
          variant: "destructive",
        });
      }
  
      // Resetear estado de carga
      setVerificationState({
        loading: false,
        codeSent: false,
        error: error.message || 'Error al enviar código'
      });
    }
  };

  const verifyEmailCode = async (code) => {
    if (code.length !== 6) return;

    try {
      setVerificationState(prev => ({ ...prev, loading: true }));

      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/verify-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          email: formData.email,
          code: code,
          userType: 'staff'
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Código inválido');
      }

      const data = await response.json();

      setFormData(prev => ({ ...prev, emailVerified: true }));
      setVerificationState({
        loading: false,
        codeSent: false,
        error: null
      });

      toast({
        title: "Email verificado",
        description: "Tu email ha sido verificado correctamente",
      });

      handleNext();

    } catch (error) {
      console.error('Error en verificación:', error);
      setVerificationState(prev => ({
        ...prev,
        loading: false,
        error: error.message
      }));

      toast({
        title: "Error",
        description: error.message || "Error al verificar el código",
        variant: "destructive",
      });
    }
  };

  const requestPhoneVerificationCode = async () => {
    if (!formData.telefono || formData.telefono.length !== 9) {
      setErrors(prev => ({ 
        ...prev, 
        telefono: 'El teléfono debe tener 9 dígitos' 
      }));
      return;
    }
  
    try {
      setLoading(true);
  
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/verification/request-sms-verification', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          telefono: formData.telefono,
          prefijo: formData.prefijo_telefono
        })
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.message || 'Error al enviar código de verificación');
      }
  
      // Actualizar estados
      setPhoneCodeSent(true);
      setTimeLeft(600); // 10 minutos
      setResendDisabled(true);
  
      toast({
        title: "Código enviado",
        description: "Te hemos enviado un código por SMS",
      });
  
      // En desarrollo, mostrar código
      if (process.env.NODE_ENV === 'development' && data.code) {
        console.log('Código de verificación (desarrollo):', data.code);
      }
  
    } catch (error) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  // Funciones de navegación
  const validateStep = () => {
    const newErrors = {};

    switch (currentStep) {
      case 1: // Personal Info
        if (!formData.nombre) newErrors.nombre = 'El nombre es requerido';
        if (!formData.fechaNacimiento) newErrors.fechaNacimiento = 'La fecha es requerida';
        if (!formData.genero) newErrors.genero = 'El género es requerido';
        if (!formData.ubicacion) {
          newErrors.location = 'Por favor seleccione una ubicación válida';
        }
        break;

      case 2: // Staff Info
        if (!formData.puesto) newErrors.puesto = 'El puesto es requerido';
        if (!formData.departamento) newErrors.departamento = 'El departamento es requerido';
        if (!formData.nivelAcceso) newErrors.nivelAcceso = 'El nivel de acceso es requerido';
        if (!formData.codigoStaff) newErrors.codigoStaff = 'El código de autorización es requerido';
        break;

      case 3: // Password
        if (!formData.password) {
          newErrors.password = 'La contraseña es requerida';
        } else if (formData.password.length < 8) {
          newErrors.password = 'La contraseña debe tener al menos 8 caracteres';
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Las contraseñas no coinciden';
        }
        break;

      case 4: // Email verification
        if (!formData.email) {
          newErrors.email = 'El email es requerido';
        } else if (!formData.emailVerified) {
          newErrors.email = 'Debe verificar su email';
        }
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      setCurrentStep(prev => prev + 1);
      setErrors({});
    }
  };

  const handleBack = () => {
    setCurrentStep(prev => prev - 1);
    setErrors({});
  };

  const handleFinish = async () => {
    if (!validateStep()) return;
  
    setLoading(true);
    try {
      const formDataToSend = new FormData();
      
      // Preparar los datos para enviar
      const dataToSend = { ...formData };
      
      // Eliminar campos que no queremos enviar
      delete dataToSend.confirmPassword;
      delete dataToSend.fotoPreview;
      
      // Procesar foto de perfil si existe
      if (dataToSend.fotoPerfil instanceof File) {
        formDataToSend.append('fotoPerfil', dataToSend.fotoPerfil);
        delete dataToSend.fotoPerfil;
      }
      
      // Agregar el resto de campos al FormData
      Object.keys(dataToSend).forEach(key => {
        if (dataToSend[key] !== null && dataToSend[key] !== undefined) {
          formDataToSend.append(key, dataToSend[key]);
        }
      });
  
      const response = await fetch('https://backend-production-1786.up.railway.app/api/auth/register/staff', {
        method: 'POST',
        body: formDataToSend
      });
  
      const data = await response.json();
  
      if (!response.ok) {
        throw new Error(data.error || 'Error en el registro');
      }
  
      // Mostrar mensaje de éxito
      toast({
        title: "Registro exitoso",
        description: "¡Te has registrado correctamente! Por favor revisa tu correo electrónico para un mensaje de bienvenida.",
        variant: "success",
      });
  
      // Login automático
      if (data.token && data.user) {
        login(data.token, data.user);
        navigate('/admin/dashboard');
      } else {
        throw new Error('Datos de autenticación inválidos');
      }
    } catch (error) {
      console.error('Error en registro:', error);
      setErrors({ submit: error.message });
      
      toast({
        title: "Error en el registro",
        description: error.message || "No se pudo completar el registro",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Renderizado de pasos
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderPersonalInfo();
      case 2:
        return renderStaffInfo();
      case 3:
        return renderPasswordStep();
      case 4:
        return renderEmailVerification();
      default:
        return null;
    }
  };

  // Componentes de renderizado de pasos
  const renderPersonalInfo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Información Personal
      </motion.h3>

      {/* Input para nombre */}
      <motion.div variants={fadeInUp} className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Nombre Completo"
          name="nombre"
          value={formData.nombre}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.nombre ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.nombre && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.nombre}</p>
        )}
      </motion.div>

      {/* Fecha de nacimiento */}
      <motion.div variants={fadeInUp} className="relative">
        <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="date"
          name="fechaNacimiento"
          value={formData.fechaNacimiento}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.fechaNacimiento ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.fechaNacimiento && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.fechaNacimiento}</p>
        )}
      </motion.div>

      {/* Género */}
      <motion.div variants={fadeInUp} className="relative">
        <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <select
          name="genero"
          value={formData.genero}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.genero ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        >
          <option value="">Seleccionar Género</option>
          <option value="masculino">Masculino</option>
          <option value="femenino">Femenino</option>
          <option value="otro">Otro</option>
        </select>
        {errors.genero && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.genero}</p>
        )}
      </motion.div>

      {/* Foto de perfil */}
      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Foto de Perfil
        </label>
        <div className="flex items-center space-x-4">
          <div className={`h-20 w-20 rounded-full flex items-center justify-center border-2 overflow-hidden ${
            formData.fotoPreview ? 'border-indigo-300' : errors.fotoPerfil ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-gray-50'
          }`}>
            {formData.fotoPreview ? (
              <img src={formData.fotoPreview} alt="Preview" className="h-full w-full object-cover" />
            ) : (
              <Camera className="h-6 w-6 text-gray-400" />
            )}
          </div>
          <div className="flex-1">
            <label className="cursor-pointer">
              <div className="px-4 py-2 text-sm bg-indigo-50 text-indigo-600 rounded-lg border border-indigo-200 hover:bg-indigo-100 transition-colors text-center">
                {formData.fotoPerfil ? 'Cambiar imagen' : 'Seleccionar imagen'}
              </div>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
            {errors.fotoPerfil && (
              <p className="mt-1 text-sm text-red-500">{errors.fotoPerfil}</p>
            )}
            <p className="text-xs text-gray-500 mt-1">
              Formatos: JPG, PNG, WEBP (máx. 5MB)
            </p>
          </div>
        </div>
      </motion.div>

      {/* Ubicación */}
      <motion.div variants={fadeInUp} className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center gap-2">
          <MapPin className="h-4 w-4 text-indigo-500" />
          Ubicación
        </label>
        <div className="relative">
          <MapPin className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
          <LocationAutocomplete 
            onLocationSelect={handleLocationSelect}
            placeholder="Buscar ubicación..."
            className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
              errors.location ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
          />
          {errors.location && (
            <p className="mt-1 text-sm text-red-500 ml-2">{errors.location}</p>
          )}
        </div>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-blue-50 rounded-xl p-4 border border-blue-100 mt-4"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-700">
            Esta información es necesaria para tu perfil de staff. Es importante que todos los datos sean correctos y verídicos.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Información específica del Staff
  const renderStaffInfo = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Información de Staff
      </motion.h3>

      {/* Puesto */}
      <motion.div variants={fadeInUp} className="relative">
        <UserCheck className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Puesto o cargo"
          name="puesto"
          value={formData.puesto}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.puesto ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.puesto && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.puesto}</p>
        )}
      </motion.div>

      {/* Departamento */}
      <motion.div variants={fadeInUp} className="relative">
        <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="text"
          placeholder="Departamento"
          name="departamento"
          value={formData.departamento}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.departamento ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.departamento && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.departamento}</p>
        )}
      </motion.div>

      {/* Nivel de Acceso */}
      <motion.div variants={fadeInUp} className="relative">
        <Key className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <select
          name="nivelAcceso"
          value={formData.nivelAcceso}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.nivelAcceso ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        >
          <option value="basico">Nivel Básico</option>
          <option value="medio">Nivel Medio</option>
          <option value="avanzado">Nivel Avanzado</option>
          <option value="admin">Administrador</option>
        </select>
        {errors.nivelAcceso && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.nivelAcceso}</p>
        )}
      </motion.div>

      {/* Código de Staff */}
      <motion.div variants={fadeInUp} className="relative">
        <Shield className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="password"
          placeholder="Código de autorización"
          name="codigoStaff"
          value={formData.codigoStaff}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.codigoStaff ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        {errors.codigoStaff && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.codigoStaff}</p>
        )}
      </motion.div>

      {/* Número de Teléfono */}
      <motion.div variants={fadeInUp} className="relative">
        <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <div className="flex">
          <div className="bg-indigo-50 flex items-center px-3 border-2 border-r-0 border-gray-200 rounded-l-xl">
            <span className="text-gray-600 text-sm">{formData.prefijo_telefono}</span>
          </div>
          <input
            type="tel"
            placeholder="Teléfono"
            name="telefono"
            value={formData.telefono}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '').slice(0, 9);
              setFormData(prev => ({ 
                ...prev, 
                telefono: value
              }));
            }}
            className={`w-full pl-4 pr-4 py-3 border-2 rounded-r-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
              errors.telefono ? 'border-red-300 bg-red-50' : 'border-gray-200'
            }`}
          />
        </div>
        {errors.telefono && (
          <p className="mt-1 text-sm text-red-500 ml-2">{errors.telefono}</p>
        )}
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Info className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            El código de autorización es requerido para verificar que tienes permiso para registrarte como staff. Este código debe ser proporcionado por un administrador del sistema.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado del paso de contraseña
  const renderPasswordStep = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Configura tu Contraseña
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type={showPassword ? "text" : "password"}
          placeholder="Contraseña"
          name="password"
          value={formData.password}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.password ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowPassword(!showPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
        {errors.password && (
          <p className="text-sm text-red-500 mt-1">{errors.password}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="relative">
        <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type={showConfirmPassword ? "text" : "password"}
          placeholder="Confirmar Contraseña"
          name="confirmPassword"
          value={formData.confirmPassword}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-12 py-3 border-2 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors duration-300 ${
            errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
        />
        <button
          type="button"
          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-indigo-500 transition-colors"
        >
          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
        </button>
        {errors.confirmPassword && (
          <p className="text-sm text-red-500 mt-1">{errors.confirmPassword}</p>
        )}
      </motion.div>

      <motion.div variants={fadeInUp} className="space-y-3 mt-4 bg-white p-4 rounded-xl border border-gray-200">
        <h4 className="text-sm font-medium text-gray-700 flex items-center gap-2">
          <Shield className="w-4 h-4 text-indigo-500" />
          Requisitos de seguridad:
        </h4>
        <ul className="space-y-2">
          <li className={`flex items-center text-sm ${formData.password.length >= 8 ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              formData.password.length >= 8 ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${formData.password.length >= 8 ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Mínimo 8 caracteres
          </li>
          <li className={`flex items-center text-sm ${/[A-Z]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              /[A-Z]/.test(formData.password) ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${/[A-Z]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Al menos una mayúscula
          </li>
          <li className={`flex items-center text-sm ${/[0-9]/.test(formData.password) ? 'text-green-600' : 'text-gray-500'}`}>
            <div className={`flex-shrink-0 w-5 h-5 mr-2 rounded-full flex items-center justify-center ${
              /[0-9]/.test(formData.password) ? 'bg-green-100' : 'bg-gray-100'
            }`}>
              <CheckCircle2 className={`w-4 h-4 ${/[0-9]/.test(formData.password) ? 'text-green-500' : 'text-gray-400'}`} />
            </div>
            Al menos un número
          </li>
        </ul>
      </motion.div>
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Shield className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            Como miembro del staff, es crucial utilizar una contraseña segura. Tu cuenta tendrá acceso a datos sensibles del sistema.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  // Renderizado de verificación de email
  const renderEmailVerification = () => (
    <motion.div 
      className="space-y-6"
      initial="hidden"
      animate="visible"
      variants={staggerContainer}
    >
      <motion.h3 
        className="text-lg font-semibold text-center mb-5 text-gray-800"
        variants={fadeInUp}
      >
        Verificación de Email
      </motion.h3>

      <motion.div variants={fadeInUp} className="relative">
        <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500" size={20} />
        <input
          type="email"
          name="email"
          placeholder="Email"
          value={formData.email}
          onChange={handleInputChange}
          className={`w-full pl-10 pr-4 py-3 border-2 rounded-xl transition-colors duration-300 ${
            formData.emailVerified ? 'border-green-500 bg-green-50' : errors.email ? 'border-red-300 bg-red-50' : 'border-gray-200'
          }`}
          disabled={formData.emailVerified || verificationState.loading}
        />
        {formData.emailVerified && (
          <CheckCircle2 className="absolute right-3 top-1/2 transform -translate-y-1/2 text-green-500" size={20} />
        )}
        {errors.email && (
          <p className="text-red-500 text-sm mt-1">{errors.email}</p>
        )}
      </motion.div>

      {!formData.emailVerified && (
        <>
          <motion.button
            variants={fadeInUp}
            onClick={requestVerificationCode}
            disabled={verificationState.loading || resendDisabled || !formData.email}
            whileHover={!verificationState.loading && !resendDisabled && formData.email ? { scale: 1.02 } : {}}
            whileTap={!verificationState.loading && !resendDisabled && formData.email ? { scale: 0.98 } : {}}
            className={`w-full py-3 rounded-xl transition-all duration-300
              ${verificationState.loading || resendDisabled || !formData.email
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-blue-600 text-white shadow-md hover:shadow-lg'
              }`}
          >
            {verificationState.loading ? (
              <div className="flex items-center justify-center">
                <span className="mr-2">Enviando...</span>
                <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              </div>
            ) : verificationState.codeSent ? (
              'Reenviar código'
            ) : (
              'Enviar código de verificación'
            )}
          </motion.button>

          {verificationState.codeSent && (
            <motion.div 
              variants={fadeInUp} 
              className="mt-4 space-y-4"
            >
              <div className="bg-white p-4 rounded-xl border border-gray-200">
                <p className="text-sm text-gray-700 mb-3 text-center">
                  Ingresa el código de 6 dígitos que enviamos a tu email
                </p>
                <input
                  type="text"
                  placeholder="Código de verificación"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                    setVerificationCode(value);
                    if (value.length === 6) {
                      verifyEmailCode(value);
                    }
                  }}
                  className="w-full px-4 py-3 border-2 rounded-xl text-center text-lg tracking-wider focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200"
                  maxLength={6}
                />
              </div>
              {timeLeft > 0 && (
                <p className="text-sm text-gray-500 text-center">
                  Podrás reenviar el código en: <span className="font-medium">{Math.floor(timeLeft / 60)}:{(timeLeft % 60).toString().padStart(2, '0')}</span>
                </p>
              )}
            </motion.div>
          )}

          {verificationState.error && (
            <motion.p 
              variants={fadeInUp}
              className="text-red-500 text-sm text-center bg-red-50 p-3 rounded-lg"
            >
              {verificationState.error}
            </motion.p>
          )}
        </>
      )}
      
      <motion.div 
        variants={fadeInUp}
        className="bg-indigo-50 rounded-xl p-4 border border-indigo-100"
      >
        <div className="flex items-start gap-3">
          <Mail className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-indigo-700">
            La verificación de tu email es fundamental para asegurar la comunicación con el equipo y la seguridad de tu cuenta de staff.
          </p>
        </div>
      </motion.div>
    </motion.div>
  );

  return (
    <BaseRegister
      currentStep={currentStep}
      totalSteps={totalSteps}
      handleNext={handleNext}
      handleBack={handleBack}
      handleFinish={handleFinish}
      loading={loading}
      title="Registro de Staff"
      errors={errors}
    >
      {renderStep()}
    </BaseRegister>
  );
};

export default StaffRegister;